// Dependencies
import { type List, type Map, fromJS } from "immutable";
import type Immutable from "immutable";
import { isImmutable } from "immutable";
import React, { useMemo } from "react";
import type { ActionMeta } from "react-select";

import { EmployeeSearchServiceQueryParams } from "@performyard/service-contracts";

// Components
import EmployeePicker, { type EmployeeOption, type SingleEmployee } from "./index";

interface BaseProps {
	disabled?: boolean;
	name?: string;
	label?: string;
	placeholder?: string;
	search?: (inputValue: string) => Promise<EmployeeOption[]>;
	onChange?: (
		value: string | Map<string, string> | List<Map<string, string>>,
		isSelected: boolean,
		selectionID?: string
	) => void;
	isSelected?: boolean;
	prependOptions?: Immutable.List<Immutable.Map<string, string>> | { id: string; name: string }[];
	results?: any;
	value?: string | Immutable.Map<string, string> | Immutable.List<Immutable.Map<string, string>>; // string or immutable map
	limit?: number;
	isMultiSelect?: boolean;
	filters?: { [key: string]: string };
	onDelete?: (selectionID: string) => void; // only used when isMultiSelect is true and used for old select picker compatibility
	isInputPool?: boolean;
	excludeCurrentUser?: boolean;
	exclusionList?: string[];
	limitToDirectReports?: boolean;
	searchServiceProps?: Partial<EmployeeSearchServiceQueryParams>;
	version?: "searchService" | "legacy";
}

/**
 * This component is used to wrap the EmployeePicker component and make it compatible with the
 * old SelectPicker component. They work differently so this component is used to make the
 * transition easier. Ideally we don't need to use this component and anything that uses it
 * should be refactored to use the EmployeePicker component directly.
 */
const CompatibilityWrapper = (props: BaseProps) => {
	const {
		disabled = false,
		placeholder = "",
		isMultiSelect = false,
		filters,
		isInputPool,
		limitToDirectReports,
	} = props;
	/**
	 * The old employee picker component could have a value of a string or an
	 * immutable map. If the value was a string, it meant there was no selection
	 * and it was just text input for the search. If the value was an immutable
	 * map, it meant there was a selection and the value was the employee's ID.
	 * @param value
	 * @param actionMeta
	 */
	const onChange = (value: SingleEmployee | SingleEmployee[], actionMeta?: ActionMeta<SingleEmployee>) => {
		switch (actionMeta?.action) {
			case "select-option":
				// to make it compatible with old select picker,
				// we need to return the one value added in multi select
				// instead of the entire array of values
				if (isMultiSelect) {
					const multiVal = value as SingleEmployee[];

					if (isInputPool) {
						props.onChange?.(fromJS(multiVal.map((val) => ({ id: val.value, name: val.label }))), true);
					} else {
						const lastVal = multiVal[multiVal.length - 1];
						props.onChange?.(fromJS({ id: lastVal.value, name: lastVal.label }), true, lastVal.value);
					}
				} else {
					const singleVal = value as SingleEmployee;
					props.onChange?.(fromJS({ id: singleVal.value, name: singleVal.label }), true, singleVal.value);
				}
				break;
			case "remove-value": {
				const multiVal = value as SingleEmployee[];
				if (isInputPool) {
					props.onChange?.(fromJS(multiVal.map((val) => ({ id: val.value, name: val.label }))), false);
				} else {
					const existingValues = props.value as any;
					const removedValue = existingValues
						.filter((item) => {
							return !multiVal.map((val) => val.value).includes(item.get("id"));
						})
						.toJS();
					props.onDelete?.(removedValue[0].id);
				}
				break;
			}
			case "clear":
				props.onChange?.("", false);
				break;
			default:
				break;
		}
	};

	/**
	 * If the value is a string, then there is no selection so there is no
	 * "value" as far as the EmployeePicker is concerned. If the value is an
	 * immutable map, then we need to convert it to a plain object so that
	 * the EmployeePicker can use it.
	 */
	const value = useMemo(() => {
		if (typeof props.value === "string") {
			return props.value;
		}

		if (isMultiSelect) {
			if (props.value) {
				return props.value
					.map((item) => {
						return {
							value: item.get("id"),
							label: item.get("name"),
						};
					})
					.toJS();
			}
		} else if (isImmutable(props.value)) {
			// Casting below should be safe. TS recognizes that props.value could be a List, but we handle
			// that case at runtime above. If it's not a List, it's a Map.
			return {
				value: (props.value as Map<string, string>).get("id"),
				label: (props.value as Map<string, string>).get("name"),
			};
		}
	}, [props.value]) as SingleEmployee | SingleEmployee[];

	let prependOptions: any[] = [];

	// if is immutable map, convert prepend options to js and format to react select values
	if (props.prependOptions && isImmutable(props.prependOptions)) {
		const immutablePrependOptions = fromJS(props.prependOptions) as Immutable.List<Immutable.Map<string, string>>;
		prependOptions = immutablePrependOptions
			.map((option) => ({ value: option.get("id"), label: option.get("name") }))
			.toJS();
	}
	// else, format prepend options to react select values
	else if (props.prependOptions && !isImmutable(props.prependOptions)) {
		const jsPrependOptions = props.prependOptions as { id: string; name: string }[];

		prependOptions = jsPrependOptions.map((option) => ({ value: option.id, label: option.name }));
	}

	return (
		<EmployeePicker
			{...props}
			value={value}
			disabled={disabled}
			onChange={onChange as any}
			placeholder={placeholder}
			prependOptions={prependOptions}
			isMultiSelect={isMultiSelect}
			filters={filters}
			limitToDirectReports={limitToDirectReports}
		/>
	);
};

export default CompatibilityWrapper;
